import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import axios from "axios";

import logo from "../../assets/img/logo.jpg";
import { API_BASE } from "../../config/api";

const buttonStyles = {
  minHeight: 48,
  borderRadius: 2,
  bgcolor: "#171310",
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#302821", boxShadow: "none" },
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirmation) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/password-reset/confirm`, { token, password });
      setSuccess(true);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "No pudimos actualizar la contraseña. Solicitá un enlace nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "#f6f3ef",
        py: 4,
        backgroundImage:
          "radial-gradient(circle at 15% 20%, rgba(202, 166, 112, .18), transparent 28%), radial-gradient(circle at 85% 80%, rgba(31, 27, 23, .08), transparent 30%)",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #e9e2da",
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 5 },
            boxShadow: "0 22px 60px rgba(45, 35, 24, .10)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box component="img" src={logo} alt="Estudio Isaac" sx={{ width: 82, height: 82, borderRadius: "50%", objectFit: "cover" }} />
          </Box>

          {success ? (
            <Box sx={{ textAlign: "center" }}>
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 60, color: "#247a47", mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Contraseña actualizada</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Ya podés ingresar a tu cuenta con tu contraseña nueva.
              </Typography>
              <Button component={RouterLink} to="/iniciar-sesión" variant="contained" fullWidth sx={buttonStyles}>
                Iniciar sesión
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <LockResetRoundedIcon sx={{ fontSize: 48, color: "#2b241e", mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Creá una contraseña nueva</Typography>
                <Typography color="text.secondary">Debe tener al menos 8 caracteres.</Typography>
              </Box>

              {!token && (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                  El enlace está incompleto. Solicitá uno nuevo para continuar.
                </Alert>
              )}
              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  autoFocus
                  fullWidth
                  required
                  label="Contraseña nueva"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            onClick={() => setShowPassword((visible) => !visible)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Repetir contraseña"
                  type={showPassword ? "text" : "password"}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoComplete="new-password"
                  sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained" fullWidth disabled={loading || !token} sx={buttonStyles}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Guardar contraseña"}
                </Button>
              </Box>

              {!token && (
                <Button component={RouterLink} to="/olvide-mi-contrasena" fullWidth sx={{ mt: 1.5, color: "#4a4037", textTransform: "none" }}>
                  Solicitar un enlace nuevo
                </Button>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
